import { Col, Row } from "antd";
import dayjs from "dayjs";
import { MdClose } from "react-icons/md";
import Button from "shared/components/Button";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { ADVANCED_FILTER_DATE_PRESET_MARK } from "utils/constants/advancedFilterDatePreset";
import {
  DATE_PRESETS_KEYS,
  FIELD_TYPES,
  NO_VALUE_OPERATORS,
  RANGE_OPERATORS,
  getDatePresetRange,
  getFieldConfig,
  getOperatorsForField,
  inferDatePresetKey,
  TEXT_SEARCH_OPERATORS,
} from "./config";

const markPresetOnDayjs = (d, key) => {
  if (d) {
    Object.defineProperty(d, ADVANCED_FILTER_DATE_PRESET_MARK, {
      value: key,
      enumerable: false,
      writable: true,
      configurable: true,
    });
  }
  return d;
};

const buildRangePresets = (t) =>
  DATE_PRESETS_KEYS.map((key) => ({
    label: t(key),
    value: () => {
      const [a, b] = getDatePresetRange(key);
      const start = a.startOf("day");
      const end = b.endOf("day");
      markPresetOnDayjs(start, key);
      markPresetOnDayjs(end, key);
      return [start, end];
    },
  }));

const buildSimplePresets = (t) =>
  DATE_PRESETS_KEYS.map((key) => ({
    label: t(key),
    value: () => {
      const d = getDatePresetRange(key)[0].startOf("day");
      return markPresetOnDayjs(d, key);
    },
  }));

const FilterRow = ({
  filter,
  index,
  availableFields,
  usedFieldKeys,
  onUpdate,
  onRemove,
  translate,
  selectionOptions,
  fields,
}) => {
  const fieldConfig = filter.fieldKey
    ? getFieldConfig(filter.fieldKey, fields)
    : null;
  const fieldType = fieldConfig?.type;
  const operators = getOperatorsForField(fieldConfig);
  const isNoValue = NO_VALUE_OPERATORS.includes(filter.operator);
  const isRange = RANGE_OPERATORS.includes(filter.operator);

  const fieldOptions = availableFields
    .filter((f) => f.key === filter.fieldKey || !usedFieldKeys.includes(f.key))
    .map((f) => {
      const Icon = f.icon;
      return {
        label: (
          <span className="advanced-filter__field-option">
            {Icon && <Icon size={14} />}
            {translate.FIELDS[f.labelKey]}
          </span>
        ),
        value: f.key,
      };
    });

  const handleFieldChange = (fieldKey) => {
    const config = getFieldConfig(fieldKey, fields);
    const firstOperator = getOperatorsForField(config)[0]?.key;
    onUpdate(index, {
      fieldKey,
      operator: firstOperator,
      value: undefined,
      value2: undefined,
      values: undefined,
      datePresetKey: undefined,
    });
  };

  const handleOperatorChange = (operator) => {
    const update = {
      operator,
      value: undefined,
      value2: undefined,
      values: undefined,
      datePresetKey: undefined,
    };
    if (fieldType === FIELD_TYPES.DATE) {
      if (isRange && !RANGE_OPERATORS.includes(operator)) {
        update.value = filter.value;
        update.value2 = filter.value2;
        update.datePresetKey = filter.datePresetKey;
      }
    }
    onUpdate(index, update);
  };

  const renderValueInput = () => {
    if (isNoValue) return null;

    if (TEXT_SEARCH_OPERATORS.includes(filter.operator)) {
      return (
        <Input
          placeholder={translate.VALUE_PLACEHOLDER}
          value={filter.value || ""}
          onChange={({ target: { value } }) => onUpdate(index, { value })}
        />
      );
    }

    switch (fieldType) {
      case FIELD_TYPES.TEXT:
        return (
          <Input
            placeholder={translate.VALUE_PLACEHOLDER}
            value={filter.value || ""}
            onChange={({ target: { value } }) => onUpdate(index, { value })}
          />
        );

      case FIELD_TYPES.NUMBER:
        if (isRange) {
          return (
            <div className="advanced-filter__range-inputs">
              <div>
                <Input
                  type="number"
                  placeholder="Min"
                  value={
                    filter.value != null ? Number(filter.value) : undefined
                  }
                  label={null}
                  onChange={({ target: { value } }) =>
                    onUpdate(index, {
                      value:
                        value !== "" && value != null
                          ? String(value)
                          : undefined,
                    })
                  }
                />
              </div>
              <div>
                <Input
                  type="number"
                  placeholder="Max"
                  value={
                    filter.value2 != null ? Number(filter.value2) : undefined
                  }
                  label={null}
                  onChange={({ target: { value } }) =>
                    onUpdate(index, {
                      value2:
                        value !== "" && value != null
                          ? String(value)
                          : undefined,
                    })
                  }
                />
              </div>
            </div>
          );
        }
        return (
          <Input
            type="number"
            placeholder={translate.VALUE_PLACEHOLDER}
            value={filter.value != null ? Number(filter.value) : undefined}
            label={null}
            onChange={({ target: { value } }) =>
              onUpdate(index, {
                value:
                  value !== "" && value != null ? String(value) : undefined,
              })
            }
          />
        );

      case FIELD_TYPES.DATE:
        if (isRange) {
          const rangeValue = filter.datePresetKey
            ? getDatePresetRange(filter.datePresetKey)
            : filter.value && filter.value2
              ? [dayjs(filter.value), dayjs(filter.value2)]
              : undefined;
          return (
            <Input
              type="date"
              variant="range"
              label={null}
              presets={buildRangePresets((k) => translate.DATE_PRESETS[k])}
              value={rangeValue}
              onChange={(dates) => {
                if (dates && dates[0] && dates[1]) {
                  const fromPreset =
                    dates[0][ADVANCED_FILTER_DATE_PRESET_MARK] ??
                    dates[1][ADVANCED_FILTER_DATE_PRESET_MARK];
                  const key =
                    fromPreset ??
                    inferDatePresetKey("between", dates[0], dates[1]);
                  onUpdate(index, {
                    datePresetKey: key,
                    value: key ? undefined : dates[0].format("YYYY-MM-DD"),
                    value2: key ? undefined : dates[1].format("YYYY-MM-DD"),
                  });
                } else {
                  onUpdate(index, {
                    value: undefined,
                    value2: undefined,
                    datePresetKey: undefined,
                  });
                }
              }}
              format="DD/MM/YYYY"
            />
          );
        }
        const simpleValue = filter.datePresetKey
          ? getDatePresetRange(filter.datePresetKey)[0]
          : filter.value
            ? dayjs(filter.value)
            : undefined;
        return (
          <Input
            type="date"
            variant="simple"
            label={null}
            presets={buildSimplePresets((k) => translate.DATE_PRESETS[k])}
            value={simpleValue}
            onChange={(date) => {
              if (date) {
                const fromPreset = date[ADVANCED_FILTER_DATE_PRESET_MARK];
                const key = fromPreset ?? inferDatePresetKey("is", date, null);
                onUpdate(index, {
                  datePresetKey: key,
                  value: key ? undefined : date.format("YYYY-MM-DD"),
                  value2: undefined,
                });
              } else {
                onUpdate(index, {
                  value: undefined,
                  datePresetKey: undefined,
                });
              }
            }}
            format="DD/MM/YYYY"
          />
        );

      case FIELD_TYPES.SELECTION: {
        const options = selectionOptions[filter.fieldKey] || [];
        return (
          <Select
            label={null}
            mode="multiple"
            placeholder={translate.VALUE_PLACEHOLDER}
            value={filter.values || []}
            onChange={(vals) => onUpdate(index, { values: vals })}
            options={options.map((opt) => ({
              label: opt.label || opt.value,
              value: opt.value,
            }))}
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
          />
        );
      }

      default:
        return null;
    }
  };

  return (
    <Row gutter={16}>
      <Col span={4}>
        <Select
          label={null}
          placeholder={translate.FIELD_PLACEHOLDER}
          value={filter.fieldKey || undefined}
          onChange={handleFieldChange}
          options={fieldOptions}
        />
      </Col>
      <Col span={4}>
        <Select
          label={null}
          placeholder={translate.OPERATOR_PLACEHOLDER}
          value={filter.operator || undefined}
          onChange={handleOperatorChange}
          disabled={!fieldType}
          options={operators.map((op) => ({
            label: translate.OPERATORS[op.labelKey],
            value: op.key,
          }))}
        />
      </Col>
      <Col span={16}>
        <div className="advanced-filter__row-value">
          {renderValueInput()}
          <Button
            type="danger--outlined"
            onClick={() => onRemove(index)}
            className="advanced-filter__remove-btn"
          >
            <MdClose size={16} />
          </Button>
        </div>
      </Col>
    </Row>
  );
};

export default FilterRow;
